import { useState, useEffect, useMemo, useRef, useCallback, useReducer } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { sortBy, isArray, uniq, isEqual, isEmpty, cloneDeep } from 'lodash'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import {
	Dialog,
	DialogContent,
	IconButton,
	Box,
	CircularProgress,
	Typography,
	DialogActions,
	Popover,
	Button,
	Stack,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid-premium'

import DeleteIcon from '@mui/icons-material/Delete'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'

import {
	TilePaginationDatagrid,
	TOOLBAR_ITEMS,
} from 'components/pages/dis-v2/_components/TileDatagrid'
import LoadingSpinner from 'custom-components/LoadingSpinner'
import DoformsRecordsActions from '../../../../../data/datagrid/DoformsRecordsActions'
import DoFormsFormTile from '../../doforms-form/DoformsFormTile'
import { SummaryStatusCard } from '../utils/components/SummaryStatusCard'
import SummaryHeaderComponent from '../utils/components/SummaryHeaderComponent'
import { FiltersSettingComponent } from '../utils/components/FiltersSettingComponent'

import { off, onValue, ref } from 'firebase/database'
import database from '../../../../../../firebase-config'

import { useRecordQuery } from '../../utils/tileRecordQuery'
import { useJobActivityViewQuery } from '../../job-summary/helper/reducers/jobAcitivityViewQuery'
import { useModalDashboardDialog } from '../../modal-dashboard/ModalDashboard'
import { useSummaryDefaultProjectFormInfo } from '../utils/hooks/summaryHeaderQuery'

import { isNullOrUndefined, sleep } from 'utils/functions/helpers'
import { getAllProjects, getProjectForms, updateColumns } from '../../../../../data/dataServices'
import { getSelectedGridRecord, parsedDataGridColumns } from '../../../../../data/dataHelpers'
import { getFilteredRows } from '../../../../../data/dataHelpers'
import {
	getDefaultOperator,
	getChildLinkedFieldsByTileKey,
} from '../../../../../../utils/functions/helpers'
import { hasDeletePermission, hasUpdatingPermission } from 'utils/functions/helpers'
import { filterRowsBySearch } from '../utils/helper'

import {
	DATA_OBJ_ACTION_TYPES,
	dataObjectReducer,
} from '../../../../../data/datagrid/CreatViewComponents/ViewDialogUtils'

import {
	SUMMARY_TYPES,
	DISPLAY_TASK_COLUMNS,
	LABEL_FOR_TYPE,
	DEFAULT_TASK_COLUMNS,
} from '../utils/constant'
import {
	DISPLAY_COLUMNS,
	COLUMNS_WITH_DEFAULT_PRESET_VALUE,
} from '../../job-summary/helper/constant'
import { PlottingMapComponent } from '../../plotting-map/PlottingMapComponent'

const useStyles = makeStyles(() => ({
	taskSummaryContainer: {
		display: 'flex',
		margin: '0 10px',
		width: '99%',
		height: '100%',
	},

	sideBarContainer: {
		display: 'flex',
		height: '100%',
		minWidth: '230px',
		justifyContent: 'center',
		alignItems: 'center',
		margin: '0 10px',
	},

	summaryContainer: {
		display: 'grid',
	},

	boxContain: {
		width: '100%',
		height: '100%',
		overflowY: 'auto',
	},

	contentContainer: {
		height: '100%',
		width: '100%',
		overflowY: 'hidden',
		overflowX: 'auto',
	},

	topNavContainer: {
		alignItems: 'center',
	},
}))

const summaryWidgets = [
	{
		label: 'Open',
		icon: 'totalJobs',
		operator: 'contains',
		value: 'Open',
		id: 'widget_pending_id',
		color: '#CC0000',
	},
	{
		label: 'In Progress',
		icon: 'inProgress',
		operator: 'contains',
		value: 'In Progress',
		id: 'widget_in_progress_id',
		color: '#FF8C00',
	},
	{
		label: 'Completed',
		icon: 'completed',
		operator: 'contains',
		value: 'Complete',
		id: 'widget_complete_id',
		color: '#009900',
	},
]
const DEFAULT_TASK_KEY_COLUMN = 'Task_Key'
const DEFAULT_TASK_STATUS_COLUMN_NAME = 'Task Status'
export function TaskSummaryComponent(props) {
	const {
		tile,
		settings,
		dashboardKeyList,
		tileWrapperHeight,
		currentDashboardKey,
		passedIdValue: parentPassedIdValue,
		modalDashboardSetting: parentModalDashboardSetting,
		isSubmitting,
		handleSubmitSetting,
	} = props
	const { environment } = useSelector((state) => state)
	const permissions = environment?.userCurrent?.rights
	const classes = useStyles()
	const dispatch = useDispatch()
	const [t] = useTranslation()

	const apiRef = useGridApiRef()
	const sideBarContainerRef = useRef(null)

	const {
		tileWidth,
		columnsChecked,
		otherOptionsChecked,
		filterField,
		progressBarField,
		showProgressBar = true, //default for this tile
		showPercentage = true, //default for this tile
		modalDashboardSetting: mainModalDashboardSetting,
		summaryAdditionalFilters,
		summaryMapGridSettings,
	} = useMemo(() => settings, [settings])

	//lanscape dialog
	const { handleOpenModalDashboardDialog, handleCloseModalDashboardDialog, ModalDashboard } =
		useModalDashboardDialog({ parentModalDashboardSetting })

	// actions section
	// const [openDialog, setOpenDialog] = useState(false)
	// const [jobIdActionValue, setJobIdActionValue] = useState(null)
	// const [populatedMessage, setPopulatedMessage] = useState([])
	// const [activityJobFieldValue, setActivityJobFieldValue] = useState(null)

	// function handleOpenDialog() {
	// 	setOpenDialog(true)
	// }

	// function handleCloseDialog() {
	// 	setActivityJobFieldValue(null)
	// 	// setJobIdActionValue(null)
	// 	// setPopulatedMessage([])
	// 	setActionRecord(null)
	// 	setOpenDialog(false)
	// }

	const [action, setAction] = useState(null)
	const [actionRecord, setActionRecord] = useState(null)
	const [open, setOpen] = useState(false)
	const [error, setError] = useState(null)

	const taskStatusColors = useMemo(() => {
		return summaryWidgets.map((widgetItem) => ({
			label: widgetItem.label,
			color: widgetItem.color,
			value: widgetItem.value,
			operator: widgetItem.operator,
		}))
	}, [])

	const handleClose = () => {
		setAction(null)
		setActionRecord(null)
		setOpen(false)
	}

	// default view hook
	const { projectLoading, selectedCustomerForm, selectedNewTaskForm, selectedJobActivityForm } =
		useSummaryDefaultProjectFormInfo({
			summaryType: SUMMARY_TYPES.TASK,
		})

	// selected task grid section
	const {
		viewData: taskViewData,
		allColumns: taskAllColumns,
		serverFilter: taskServerFilter,
		viewLoading: taskViewLoading,
	} = useJobActivityViewQuery({
		formKey: selectedJobActivityForm?.formKey,
		projectKey: selectedJobActivityForm?.projectKey,
		columnsChecked,
		tile,
	})

	const defaultAllColumns = useMemo(() => {
		if (isEmpty(taskAllColumns)) return DEFAULT_TASK_COLUMNS

		return taskAllColumns
	}, [taskAllColumns])

	// sidebar section
	const [selectedJobStatusInfo, setSelectedJobStatusInfo] = useState(null)

	//search section
	const [filterText, setFilterText] = useState(null)
	const [searchText, setSearchText] = useState(null)

	const statusField = useMemo(() => {
		const a = defaultAllColumns?.find(
			(col) => col.title?.toLowerCase() === DEFAULT_TASK_STATUS_COLUMN_NAME.toLowerCase()
		)
		return a?.name
	}, [defaultAllColumns])

	const [gridColumns, setGridColumns] = useState([])
	const [currentRecords, setCurrentRecords] = useState([])

	const parentFilters = useMemo(() => {
		if (isEmpty(gridColumns) || isEmpty(parentModalDashboardSetting)) return []

		const linkedFields = getChildLinkedFieldsByTileKey(tile?.key, parentModalDashboardSetting) || []

		const childLinkedFilters = linkedFields
			?.map((field, index) => {
				const column = gridColumns.find((col) => col.field === field)
				if (!column) return null

				const defaultOperator = getDefaultOperator(column.orgType, true)
				return {
					field,
					operator: parentPassedIdValue === '' ? 'isEmpty' : defaultOperator,
					value: parentPassedIdValue,
					type: column.type,
				}
			})
			.filter((item) => !!item)

		return childLinkedFilters
	}, [parentPassedIdValue, parentModalDashboardSetting, gridColumns, tile?.key])

	const DEFAULT_ADDITIONAL_FILTERS = useMemo(() => {
		return {
			field: 'Task_Start_Date',
			type: 'DATE',
			operator: 'is',
			value: moment().startOf('day').toISOString(), // TODAY start of day
			logicOperator: 'and',
		}
	}, [])

	const filterModel = useMemo(() => {
		const filters = []
		if (filterText) {
			filters.push(filterText)
		}

		if (selectedJobStatusInfo && selectedJobStatusInfo.operator !== 'total') {
			filters.push(selectedJobStatusInfo)
		}

		if (!isEmpty(summaryAdditionalFilters)) {
			filters.push(...summaryAdditionalFilters)
		}

		return filters
	}, [filterText, selectedJobStatusInfo, summaryAdditionalFilters])

	const tileLoading = useMemo(() => {
		return projectLoading || taskViewLoading
	}, [projectLoading, taskViewLoading])

	const [isLoading, setIsLoading] = useState(false)

	// for Task view
	useEffect(() => {
		if (taskViewLoading || !taskViewData || isEmpty(taskAllColumns)) return

		// set the grid columns based on the view data
		async function setViewColumnsFromTileSettings() {
			await updateColumns({
				viewKey: taskViewData?.key,
				columnsArr: taskAllColumns,
				viewSession: true,
				token: environment?.apiToken,
			})

			setIsLoading(false)
		}
		try {
			setIsLoading(true)
			setViewColumnsFromTileSettings()
		} catch (error) {
			console.error('Error setting view columns:', error)
		}
	}, [taskViewData, taskAllColumns, taskViewLoading])

	const selectedTaskFormQueryCondition = useMemo(() => {
		if (taskViewLoading || !taskViewData) return []
		const currentConditions = cloneDeep(taskViewData?.queries?.[0]?.filter?.conditions)
		const updatedConditions = currentConditions.map((condition) => {
			if (!COLUMNS_WITH_DEFAULT_PRESET_VALUE.includes(condition.target)) return condition

			delete condition['values']
			condition.preset = 'ALL'
			return condition
		})

		return updatedConditions
	}, [taskViewData, taskViewLoading])

	const { records, recordsLoading, refetchRecords } = useRecordQuery({
		viewKey: taskViewData?.key,
		viewType: taskViewData?.type,
		clientFilter: { conditions: selectedTaskFormQueryCondition } ?? {},
		viewSession: true,
		isUpdateColumnsSession: isLoading,
	})

	useEffect(() => {
		const progressBarSettings =
			!showProgressBar && !showPercentage ? {} : { showProgressBar, showPercentage }
		const parsedGridColumns = parsedDataGridColumns(
			environment,
			defaultAllColumns,
			[statusField],
			taskStatusColors,
			[progressBarField],
			progressBarSettings
		)

		const formattedgridColumns = parsedGridColumns.map((column) => {
			if (DISPLAY_COLUMNS[column.field]) {
				return {
					...column,
					headerName: DISPLAY_COLUMNS[column.field],
				}
			}
			return column
		})

		const ordered = DISPLAY_TASK_COLUMNS.map((name) =>
			formattedgridColumns.find((col) => col.field === name)
		).filter(Boolean)

		if (isEqual(gridColumns, ordered)) return

		setGridColumns(ordered)
	}, [
		JSON.stringify(defaultAllColumns),
		statusField,
		progressBarField,
		taskStatusColors,
		showProgressBar,
		showPercentage,
	])

	useEffect(() => {
		if (recordsLoading) return
		if (isEqual(records, currentRecords)) return
		setCurrentRecords(records)
	}, [recordsLoading, records])

	const currentRows = useMemo(() => {
		if (!currentRecords?.length || !defaultAllColumns?.length) return []

		const allRows = getSelectedGridRecord({
			records: currentRecords,
			columns: defaultAllColumns,
			environment,
			columnCheckList: defaultAllColumns,
			allColumns: defaultAllColumns,
		})

		const formattedRows = allRows.map((record) => {
			const currentRecord = { ...record }
			currentRecord[statusField] = record[statusField] === 'Pending' ? 'Open' : record[statusField]
			return currentRecord
		})

		const filters = [...taskServerFilter]
		if (!isEmpty(parentFilters)) {
			filters.push(...parentFilters)
		}

		if (!isEmpty(summaryAdditionalFilters)) {
			filters.push(...summaryAdditionalFilters)
		}

		const filteredRows = getFilteredRows(filters, formattedRows)

		return filteredRows
	}, [
		currentRecords,
		taskServerFilter,
		parentFilters,
		JSON.stringify(defaultAllColumns),
		summaryAdditionalFilters,
	])

	const gridDisplayColumnFields = useMemo(() => {
		return gridColumns
			.filter((col) => col.field !== DEFAULT_TASK_KEY_COLUMN)
			.reduce((prev, col) => {
				prev.push(col.field)
				return prev
			}, [])
	}, [gridColumns])

	const displayRows = useMemo(() => {
		const filteredRows = getFilteredRows(filterModel, currentRows)
		return filterRowsBySearch({
			filteredRows,
			searchText,
			availableColumns: gridDisplayColumnFields,
		})
	}, [currentRows, filterModel, gridDisplayColumnFields, searchText])

	const gridColumnsWithActionField = useMemo(() => {
		if (isEqual(gridColumns, [])) return []
		const actionField = {
			field: 'actions',
			type: 'actions',
			width: 75,
			disableReorder: true,
			getActions: (params) => {
				const loading = [
					<Box sx={{ pl: 1.4, position: 'relative', top: '3px', left: '-6px' }}>
						<CircularProgress color="inherit" size={17} />
					</Box>,
				]

				let popupActions = []

				if (
					!environment.isReadOnlySubmission &&
					permissions &&
					hasUpdatingPermission(permissions)
				) {
					popupActions = [
						...popupActions,
						<GridActionsCellItem
							key={params.id}
							icon={<EditIcon />}
							label={t('common:misc.edit')}
							onClick={() => {
								// setPopulatedMessage([
								// 	{
								// 		field: DEFAULT_TASK_KEY_COLUMN,
								// 		value: params?.row?.[DEFAULT_TASK_KEY_COLUMN] || '',
								// 	},
								// ])
								// handleOpenDialog()
							}}
							showInMenu
						/>,
					]
				}

				if (!environment.isReadOnlySubmission && permissions && hasDeletePermission(permissions)) {
					popupActions = [
						...popupActions,
						<GridActionsCellItem
							key={params.id}
							icon={<DeleteIcon />}
							label={t('common:misc.delete')}
							onClick={handleAction(params, 'delete', records, taskViewData)}
							showInMenu
						/>,
					]
				}

				const allActions = [
					...(params?.row?.isLoading ? loading : []),
					...(!params?.row?.isLoading ? popupActions : []),
				]
				const AUTO_GENERATED_FOOTER_FROM_AGGREGATION = 'auto-generated-group-footer-root'

				// Remove the action menu icon from the aggregate row
				if (params?.id === AUTO_GENERATED_FOOTER_FROM_AGGREGATION) {
					return []
				}

				return allActions
			},
		}

		return [actionField, ...gridColumns]
	}, [environment, records, gridColumns])

	const intervalIdRef = useRef(null)

	function start() {
		if (intervalIdRef.current === null) {
			intervalIdRef.current = setTimeout(() => {
				refetchRecords()
				intervalIdRef.current = null
			}, 10000)
		}
	}

	function stop() {
		if (intervalIdRef.current !== null) {
			clearInterval(intervalIdRef.current)
			intervalIdRef.current = null
		}
	}

	function refetchAndStopTimeout() {
		refetchRecords()
		stop()
	}

	useEffect(() => {
		const customerKey = environment.account.key
		if (!customerKey) return
		const customerCountRef = ref(database, `userSessions/customerKey/${customerKey}`)
		const viewRef = ref(database, `userSessions/viewActivity/customerKey/${customerKey}`)

		const customerUnsubscribe = onValue(customerCountRef, (snapshot) => {
			// const data = snapshot.val()
			// const currentDateTime = moment().add(serverOffset, 'milliseconds').format('MM/DD/YYYY HH:mm:ss')

			// if (data && areDatesEqual(data?.Updated, currentDateTime, 'MM/DD/YYYY HH:mm:ss')) {
			sleep(2000).then(() => {
				refetchAndStopTimeout()
			})
			// }
		})

		const viewUnsubscribe = onValue(viewRef, (snapshot) => {
			sleep(2000).then(() => {
				refetchAndStopTimeout()
			})
		})

		return () => {
			off(customerCountRef)
			off(viewRef)

			customerUnsubscribe()
			viewUnsubscribe()
		}
	}, [environment.account.key])

	useEffect(() => {
		const formKey = selectedJobActivityForm?.formKey
		const projectKey = selectedJobActivityForm?.projectKey

		if (formKey && projectKey) {
			const formRef = ref(database, 'formSessions/' + projectKey + '/' + formKey)

			const unsubscribe = onValue(formRef, (snapshot) => {
				// Process data when there is a change
				sleep(2000).then(() => {
					refetchAndStopTimeout()
				})
			})

			// Returns the off function to turn off event listening when the component unmounts
			return () => {
				off(formRef)
				unsubscribe()
			}
		}
	}, [JSON.stringify(selectedJobActivityForm)])

	const handleAction = useCallback(
		(params, action, records, viewData) => async () => {
			try {
				let record = records[params.id - 1]
				record = {
					...record,
					key:
						record.sourceType === 'SUBMISSION' || record.sourceType === 'ACTIVITY'
							? record.submissionKey
							: record.dispatchKey,
				}

				if (
					['edit', 'view', 'delete'].includes(action) &&
					('activityKey' in record || 'key' in record)
				) {
					/*var key = record.key ? record.key : record.activityKey;
                        const response = await getFormSelectedFromActivityKey(
                            record.key,
                            environment.apiToken
                        )
    
                        const currentFormSelected = response.data;
                        if (Object.keys(currentFormSelected).length > 0) {
                            switch (tab) {
                                case 'views':
                                    dispatch({
                                        type: VIEWS_ACTIONS.UPDATE_VIEW_SELECTED_KEY,
                                        payload: currentFormSelected.formKey,
                                    })
                                    break
                                case 'devices':
                                    dispatch({
                                        type: DEVICES_ACTIONS.FORM_SELECTED,
                                        payload: currentFormSelected,
                                    })
                                    break
                                case 'activity':
                                    dispatch({
                                        type: ACTIVITY_ACTIONS.FORM_SELECTED,
                                        payload: currentFormSelected,
                                    })
                                    break
                                default:
                                    break
                            }
                            
                        }*/
					setAction(action)
					setActionRecord(record)
					setOpen(action !== 'images' ? true : false)
				}
			} catch (err) {
				if (err?.response?.data?.code === 404) {
					setError(`${t('common:misc.record')} ${t('common:misc.hasBeenDeleted')}`)
				} else {
					setError('Code ' + err?.response?.data?.code + ': ' + err?.response?.data?.message)
				}
			}
		},
		[action, records]
	)

	// MAP BUTTON STATE
	const hasTaskMapMountedRef = useRef(false)
	const [openMapDialog, setOpenMapDialog] = useState(false)
	const [gridFilterModel, setGridFilterModel] = useState({ items: [] })
	const [gridRowGroupingModel, setGridRowGroupingModel] = useState([])
	const [gridSortModel, setGridSortModel] = useState([])
	const [gridAggregationModel, setGridAggregationModel] = useState({})
	const [gridColumnVisibilityModel, setGridColumnVisibilityModel] = useState({})
	const [gridColumnsWidth, setGridColumnsWidth] = useState({})
	const [gridColumnsOrder, setGridColumnsOrder] = useState([])

	useEffect(() => {
		const config = settings?.summaryMapGridSettings

		if (!config) return

		setGridFilterModel(config.gridFilterModel || { items: [] })
		setGridRowGroupingModel(config.gridRowGroupingModel || [])
		setGridSortModel(config.gridSortModel || [])
		setGridAggregationModel(config.gridAggregationModel || {})
		setGridColumnVisibilityModel(config.gridColumnVisibilityModel || {})
		setGridColumnsWidth(config.gridColumnsWidth || {})
		setGridColumnsOrder(config.gridColumnsOrder || {})

		// Reset mount ref so skip-first-sync works again
		hasTaskMapMountedRef.current = false
	}, [settings?.summaryMapGridSettings])

	const handleSaveSummaryMapDatagridSetting = () => {
		const summaryMapGridSettings = {
			gridFilterModel,
			gridRowGroupingModel,
			gridSortModel,
			gridAggregationModel,
			gridColumnVisibilityModel,
			gridColumnsWidth,
			gridColumnsOrder,
		}

		if (isEqual(summaryMapGridSettings, settings?.summaryMapGridSettings)) return

		handleSubmitSetting({
			...settings,
			summaryMapGridSettings,
		})
	}

	function handleOpenMapDialog() {
		setOpenMapDialog(true)
	}

	function handleCloseMapDialog(shouldUseInterval = false) {
		handleSaveSummaryMapDatagridSetting()
		setOpenMapDialog(false)
		hasTaskMapMountedRef.current = false
	}

	const handleFilterModelChange = useCallback(
		(model) => {
			if (!hasTaskMapMountedRef.current) {
				hasTaskMapMountedRef.current = true

				if (isEmpty(model?.items) && !isEmpty(gridFilterModel?.items)) return

				setGridFilterModel(model)
				return
			}

			setGridFilterModel(model)
		},
		[hasTaskMapMountedRef, gridFilterModel, defaultAllColumns]
	)

	const handleRowGroupingModelChange = useCallback(
		(model) => {
			if (!hasTaskMapMountedRef.current) {
				hasTaskMapMountedRef.current = true

				if (isEmpty(model) && !isEmpty(gridRowGroupingModel)) return

				setGridRowGroupingModel(model)
				return
			}

			setGridRowGroupingModel(model)
		},
		[hasTaskMapMountedRef, gridRowGroupingModel]
	)

	const handleSortModelChange = useCallback(
		(model) => {
			if (!hasTaskMapMountedRef.current) {
				// First call — Grid empty sync — skip
				hasTaskMapMountedRef.current = true
				if (isEmpty(model) && !isEmpty(gridSortModel)) return

				setGridSortModel(model)
				return
			}
			setGridSortModel(model)
		},
		[hasTaskMapMountedRef, gridSortModel]
	)

	const handleAggregationModelChange = useCallback(
		(model) => {
			if (!hasTaskMapMountedRef.current) {
				hasTaskMapMountedRef.current = true

				if (isEmpty(model) && !isEmpty(gridAggregationModel)) return

				setGridAggregationModel(model)
				return
			}

			setGridAggregationModel(model)
		},
		[hasTaskMapMountedRef, gridAggregationModel]
	)

	const handleColumnVisibilityModelChange = useCallback(
		(model) => {
			if (!hasTaskMapMountedRef.current) {
				hasTaskMapMountedRef.current = true

				if (isEmpty(model) && !isEmpty(gridColumnVisibilityModel)) return

				setGridColumnVisibilityModel(model)
				return
			}

			setGridColumnVisibilityModel(model)
		},
		[hasTaskMapMountedRef, gridColumnVisibilityModel]
	)

	// Filter dialog
	const [openFilterDialog, setOpenFilterDialog] = useState(false)
	const [filterAnchorEl, setFilterAnchorEl] = useState(null)
	const [additionalFilter, additionalFilterDispatch] = useReducer(dataObjectReducer, [])

	const handleOpenFilterDialog = (event) => {
		event.preventDefault()
		initAdditionalFilters(summaryAdditionalFilters)
		setOpenFilterDialog(true)
		setFilterAnchorEl(event.currentTarget)
	}

	const handleCloseFilterDialog = () => {
		setOpenFilterDialog(false)
		setFilterAnchorEl(null)
	}

	function initAdditionalFilters(summaryAdditionalFilters) {
		if (isEmpty(summaryAdditionalFilters)) {
			additionalFilterDispatch({
				type: DATA_OBJ_ACTION_TYPES.INIT_DATA,
				initData: [DEFAULT_ADDITIONAL_FILTERS],
			})
			return
		}

		additionalFilterDispatch({
			type: DATA_OBJ_ACTION_TYPES.INIT_DATA,
			initData: summaryAdditionalFilters,
		})
	}

	useEffect(() => {
		initAdditionalFilters(summaryAdditionalFilters)
	}, [summaryAdditionalFilters])

	return tileLoading ? (
		<LoadingSpinner />
	) : (
		<div className={classes.taskSummaryContainer}>
			<div ref={sideBarContainerRef} className={classes.sideBarContainer}>
				<div className={classes.summaryContainer}>
					{!isEmpty(summaryWidgets) && (
						<div style={{ marginBottom: '15px' }}>
							<Typography
								variant="body1"
								style={{ textAlign: 'start', fontWeight: 'bolder', marginLeft: '5px' }}
							>
								{t(LABEL_FOR_TYPE[SUMMARY_TYPES.TASK])} Status
							</Typography>
							{summaryWidgets?.map((widgetItem, index) => {
								const formatedWidgetItem = { ...widgetItem, field: statusField }
								return (
									<SummaryStatusCard
										widgetItem={formatedWidgetItem}
										selectedStatusInfo={selectedJobStatusInfo}
										allRows={currentRows}
										onActionAreaClick={() =>
											setSelectedJobStatusInfo((prev) =>
												prev?.id === widgetItem.id ? null : formatedWidgetItem
											)
										}
									/>
								)
							})}
						</div>
					)}
				</div>
			</div>
			<div id="contentContainer" className={classes.contentContainer}>
				<div
					style={{
						marginRight: '5px',
						width: 'calc(100% - 5px)',
					}}
				>
					<SummaryHeaderComponent
						tile={tile}
						allColumns={defaultAllColumns}
						filterField={filterField}
						setFilterText={setFilterText}
						setSearchText={setSearchText}
						startInterval={start}
						headerType={SUMMARY_TYPES.TASK}
						selectedSummaryForm={selectedNewTaskForm}
						selectedCustomerForm={selectedCustomerForm}
						onMapButtonClick={() => {
							handleOpenMapDialog()
						}}
						onFilterButtionClick={handleOpenFilterDialog}
					/>
				</div>

				<TilePaginationDatagrid
					datagridStyle={{
						marginLeft: '8px',
						maxHeight: `${tileWrapperHeight - 60}px`,
					}}
					isLoading={recordsLoading}
					tile={tile}
					rows={displayRows}
					columns={gridColumnsWithActionField}
					apiRef={apiRef}
					onCellClick={(params, event) => {
						if (params.field === 'actions') return

						const passedIdValue = params.row?.[mainModalDashboardSetting?.dataField]
						handleOpenModalDashboardDialog({
							passedIdValue,
							modalDashboardSetting: mainModalDashboardSetting,
						})
					}}
					initGridColumnVisibilityModel={{ [DEFAULT_TASK_KEY_COLUMN]: false }}
				/>

				{/* <Dialog
					fullWidth={true}
					maxWidth="md"
					aria-labelledby="transition-modal-title"
					aria-describedby="transition-modal-description"
					className={classes.dialog}
					sx={{
						'& .MuiDialog-paper': {
							height: '90% !important',
						},
					}}
					open={openDialog}
					closeAfterTransition
				>
					<DialogTitle id="record-dialog-title">
						<div style={{ float: 'left', display: 'inline' }}>
							{isNullOrUndefined(activityJobFieldValue)
								? populatedMessage
									? t('common:input.addTask')
									: t('common:input.jobTasks')
								: t('common:misc.history')}
						</div>
						<div style={{ float: 'right', display: 'inline' }}>
							<IconButton onClick={(e) => handleCloseDialog()} alignItems="flex-end">
								<CloseIcon />
							</IconButton>
						</div>
					</DialogTitle>
					<DialogContent>
						{selectedJobForm &&
							selectedJobActivityForm &&
							!isNullOrUndefined(activityJobFieldValue) && (
								<JobActivityViewComponent
									tile={tile}
									selectedJobForm={selectedJobForm}
									selectedJobActivityForm={selectedJobActivityForm}
									activityJobFieldValue={activityJobFieldValue}
								/>
							)}
						{selectedJobActivityForm && jobIdActionValue && (
							<JobActivityGridComponent
								tile={tile}
								selectedJobActivityForm={selectedJobActivityForm}
								selectedJobIdSearch={jobIdActionValue}
							/>
						)}
					</DialogContent>
				</Dialog> */}

				{action && (
					<DoformsRecordsActions
						formSelected={{
							...selectedJobActivityForm,
							key: selectedJobActivityForm?.formKey,
							name: selectedJobActivityForm?.formName,
						}}
						setExternalError={setError}
						open={open}
						action={action}
						setAction={setAction}
						environment={environment}
						actionRecord={actionRecord}
						name={taskViewData.name}
						tab={'tile'}
						columns={gridColumns}
						onClose={handleClose}
						setGridRows={setCurrentRecords}
						refreshData={refetchRecords}
					/>
				)}

				{mainModalDashboardSetting && <ModalDashboard />}

				<Dialog
					sx={{
						'& .MuiDialogContent-root': {
							padding: '0 !important',
						},
					}}
					open={openMapDialog}
					onClose={handleCloseMapDialog}
					// fullWidth + maxWidth to let us control the paper size
					fullWidth
					// Make the paper wide (landscape) and give it a fixed-ish height
					PaperProps={{
						sx: {
							overflowY: 'unset',
							maxWidth: '90% !important',
							width: '95%',
							height: '80%',
							display: 'flex',
							backgroundColor: '#ffffff',
						},
					}}
				>
					<DialogContent>
						<PlottingMapComponent
							tile={tile}
							selectedTaskMapForm={selectedNewTaskForm}
							settings={settings}
							currentDashboardKey={currentDashboardKey}
							useTileWrapper={true}
							gridFilterModel={gridFilterModel}
							onGridFilterModelChange={handleFilterModelChange}
							gridRowGroupingModel={gridRowGroupingModel}
							onGridRowGroupingModelChange={handleRowGroupingModelChange}
							gridSortModel={gridSortModel}
							onGridSortModelChange={handleSortModelChange}
							gridAggregationModel={gridAggregationModel}
							onGridAggregationModelChange={handleAggregationModelChange}
							gridColumnVisibilityModel={gridColumnVisibilityModel}
							onGridColumnVisibilityModelChange={handleColumnVisibilityModelChange}
							columnsWidth={gridColumnsWidth}
							onGridColumnResize={(value) => setGridColumnsWidth(value)}
							columnsOrder={gridColumnsOrder}
							onGridColumnOrderChange={(value) => setGridColumnsOrder(value)}
							hasSelectColumns
						/>
					</DialogContent>
					<DialogActions>
						<IconButton
							sx={{
								position: 'absolute',
								left: '98.5%',
								top: '-3%',
								backgroundColor: '#ffffff',
								border: '2px solid',
								borderColor: 'lightgray',
								'&:hover': { backgroundColor: '#ffffff' },
							}}
							onClick={handleCloseMapDialog}
							alignItems="flex-end"
							size="small"
						>
							<CloseIcon />
						</IconButton>
					</DialogActions>
				</Dialog>

				<Popover
					anchorEl={filterAnchorEl}
					id={'my-profile'}
					keepMounted
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'start',
					}}
					open={openFilterDialog}
					onClose={handleCloseFilterDialog}
					slotProps={{
						paper: {
							style: {
								width: '600px',
								maxWidth: '45vw',
								maxHeight: '55vh',
							},
						},
					}}
				>
					<Stack
						sx={{
							'& .MuiInputBase-input': {
								fontSize: '0.9rem !important',
							},
							'& .MuiCheckbox-root': {
								p: 0,
							},
						}}
					>
						<Box sx={{ px: 1, mt: 2 }}>
							<FiltersSettingComponent
								additionalFilter={additionalFilter}
								additionalFilterDispatch={additionalFilterDispatch}
								listActiveFieldsOptions={defaultAllColumns}
								allColumns={defaultAllColumns}
							/>
						</Box>
						<Stack direction="row" justifyContent="flex-end" padding={'10px'}>
							<Stack spacing={2} direction="row">
								<Button
									size="small"
									variant="contained"
									onClick={() => {
										handleSubmitSetting({ ...settings, summaryAdditionalFilters: additionalFilter })
										handleCloseFilterDialog()
									}}
									disabled={isSubmitting}
									startIcon={isSubmitting ? <CircularProgress color="inherit" size="1em" /> : null}
								>
									{t('common:misc.ok')}
								</Button>
								<Button size="small" variant="text" onClick={handleCloseFilterDialog}>
									{t('common:misc.cancel')}
								</Button>
							</Stack>
						</Stack>
					</Stack>
				</Popover>
			</div>
		</div>
	)
}
